import { useState, useEffect, useMemo } from 'react';
import SectionHeader from '../../components/admin/SectionHeader';
import DataTable from '../../components/admin/DataTable';

const STATUS_LABELS = { active: 'Live', coming_soon: 'Coming Soon', presale: 'Pre-sale', maintenance: 'Maintenance' };
const STATUS_COLORS = { active: 'var(--admin-success)', coming_soon: '#f59e0b', presale: '#40E0D0', maintenance: '#6b7280' };

export default function AdminWaitlist({ apiFetch, token, toast }) {
  const [entries, setEntries]             = useState([]);
  const [services, setServices]           = useState([]);
  const [counts, setCounts]               = useState([]);
  const [loading, setLoading]             = useState(false);
  const [serviceFilter, setServiceFilter] = useState('all');

  // Notify modal
  const [notifyService, setNotifyService]   = useState(null);
  const [notifySubject, setNotifySubject]   = useState('');
  const [notifyMessage, setNotifyMessage]   = useState('');
  const [notifyLaunchUrl, setNotifyLaunchUrl] = useState('');
  const [notifySending, setNotifySending]   = useState(false);

  // Service quick-edit
  const [editingService, setEditingService] = useState(null);
  const [editFields, setEditFields]         = useState({});
  const [savingService, setSavingService]   = useState(false);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [wRes, sRes] = await Promise.all([
        apiFetch('/api/admin/services/waitlist'),
        apiFetch('/api/admin/services'),
      ]);
      const wData = await wRes.json();
      const sData = await sRes.json();
      setEntries(Array.isArray(wData.entries) ? wData.entries : []);
      setCounts(Array.isArray(wData.counts) ? wData.counts : []);
      setServices(Array.isArray(sData) ? sData : []);
    } catch { toast.error('Error', 'Failed to load waitlist data'); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadAll(); }, []);

  const managedServices = useMemo(() =>
    services.filter(s => s.status === 'coming_soon' || s.status === 'presale'), [services]);

  const serviceKeysWithEntries = useMemo(() =>
    [...new Set(entries.map(e => e.service_key))], [entries]);

  const filteredEntries = useMemo(() =>
    serviceFilter === 'all' ? entries : entries.filter(e => e.service_key === serviceFilter),
    [entries, serviceFilter]);

  const deleteEntry = async (id) => {
    if (!window.confirm('Remove this entry from the waitlist?')) return;
    try {
      await apiFetch(`/api/admin/services/waitlist/${id}`, { method: 'DELETE' });
      setEntries(prev => prev.filter(e => e.id !== id));
      toast.success('Removed', 'Entry deleted');
    } catch { toast.error('Error', 'Failed to delete entry'); }
  };

  const clearServiceWaitlist = async (key) => {
    const svc = services.find(s => s.key === key);
    if (!window.confirm(`Clear ALL waitlist entries for ${svc?.name || key}? This cannot be undone.`)) return;
    try {
      await apiFetch(`/api/admin/services/${key}/waitlist`, { method: 'DELETE' });
      setEntries(prev => prev.filter(e => e.service_key !== key));
      toast.success('Cleared', `All entries for ${svc?.name || key} removed`);
    } catch { toast.error('Error', 'Failed to clear waitlist'); }
  };

  const exportCSV = () => {
    apiFetch(`/api/admin/services/waitlist/export${serviceFilter !== 'all' ? `?service=${serviceFilter}` : ''}`)
      .then(r => r.blob())
      .then(blob => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = serviceFilter !== 'all' ? `waitlist-${serviceFilter}.csv` : 'waitlist-all.csv';
        document.body.appendChild(a); a.click();
        document.body.removeChild(a); URL.revokeObjectURL(url);
      })
      .catch(() => toast.error('Error', 'Failed to export CSV'));
  };

  const openNotify = (serviceKey) => {
    const svc = services.find(s => s.key === serviceKey);
    setNotifyService({ key: serviceKey, name: svc?.name || serviceKey });
    setNotifySubject(`${svc?.name || serviceKey} is now available!`);
    setNotifyMessage('');
    setNotifyLaunchUrl('');
  };

  const sendNotify = async () => {
    if (!notifySubject.trim() || !notifyMessage.trim()) {
      toast.error('Error', 'Subject and message are required'); return;
    }
    setNotifySending(true);
    try {
      const res = await apiFetch(`/api/admin/services/${notifyService.key}/waitlist/notify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ subject: notifySubject, message: notifyMessage, launchUrl: notifyLaunchUrl }),
      });
      const data = await res.json();
      if (!res.ok) { toast.error('Error', data.error || 'Failed to send'); return; }
      toast.success('Sent!', `${data.sent} email${data.sent !== 1 ? 's' : ''} sent${data.failed ? `, ${data.failed} failed` : ''}`);
      setNotifyService(null);
    } catch { toast.error('Error', 'Failed to send notifications'); }
    finally { setNotifySending(false); }
  };

  const openServiceEdit = (svc) => {
    setEditingService(svc.key);
    setEditFields({ status: svc.status || 'active', badge: svc.badge || '', launch_date: svc.launch_date || '' });
  };

  const saveServiceEdit = async (key) => {
    setSavingService(true);
    try {
      const svc = services.find(s => s.key === key);
      await apiFetch(`/api/admin/services/${key}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...svc, ...editFields }),
      });
      setServices(prev => prev.map(s => s.key === key ? { ...s, ...editFields } : s));
      setEditingService(null);
      toast.success('Saved', 'Service updated');
    } catch { toast.error('Error', 'Failed to save service'); }
    finally { setSavingService(false); }
  };

  const columns = [
    { key: 'email', header: 'Email', render: r => <strong style={{ fontSize: '13px' }}>{r.email}</strong> },
    { key: 'name', header: 'Name', render: r => r.name || <span style={{ color: 'var(--admin-text-muted)', fontStyle: 'italic' }}>—</span> },
    { key: 'service_key', header: 'Service', render: r => {
      const svc = services.find(s => s.key === r.service_key);
      return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
          <code style={{ fontSize: '11px', background: 'var(--admin-elevated)', padding: '2px 6px', borderRadius: 4 }}>{r.service_key}</code>
          {svc?.name && <span style={{ color: 'var(--admin-text-secondary)', fontSize: '11px' }}>{svc.name}</span>}
        </span>
      );
    }},
    { key: 'notes', header: 'Notes', render: r => r.notes
      ? <span style={{ fontSize: '12px', color: 'var(--admin-text-secondary)', maxWidth: 200, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.notes}>{r.notes}</span>
      : <span style={{ color: 'var(--admin-text-muted)' }}>—</span>
    },
    { key: 'created_at', header: 'Signed Up', render: r => (
      <span style={{ color: 'var(--admin-text-secondary)', fontSize: '12px' }}>
        {new Date(r.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
      </span>
    )},
    { key: 'actions', header: '', render: r => (
      <button className="admin-btn admin-btn--sm admin-btn--danger" onClick={() => deleteEntry(r.id)}>
        <i className="fa-solid fa-trash"></i>
      </button>
    )},
  ];

  return (
    <div>
      <SectionHeader title="Waitlist & Pre-launch" />

      {/* Stat cards */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
        <div className="admin-stat-card" style={{ minWidth: '130px' }}>
          <div className="admin-stat-value">{entries.length}</div>
          <div className="admin-stat-label">Total Signups</div>
        </div>
        {counts.map(c => {
          const svc = services.find(s => s.key === c.service_key);
          return (
            <div key={c.service_key} className="admin-stat-card" style={{ minWidth: '130px', cursor: 'pointer' }}
              onClick={() => setServiceFilter(c.service_key)}>
              <div className="admin-stat-value">{c.count}</div>
              <div className="admin-stat-label">{svc?.name || c.service_key}</div>
            </div>
          );
        })}
      </div>

      {/* Pre-launch service cards */}
      {managedServices.length > 0 && (
        <div className="admin-card" style={{ marginBottom: '20px' }}>
          <h3 className="admin-card-title" style={{ marginBottom: '14px' }}>Pre-launch Services</h3>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            {managedServices.map(svc => {
              const svcCount = counts.find(c => c.service_key === svc.key);
              const isEditing = editingService === svc.key;
              return (
                <div key={svc.key} style={{
                  background: 'var(--admin-elevated)', border: '1px solid var(--admin-border)',
                  borderRadius: '10px', padding: '16px', minWidth: '220px', flex: '1',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: '14px', color: 'var(--admin-text-primary)' }}>{svc.name}</div>
                      <code style={{ fontSize: '10px', color: 'var(--admin-text-muted)' }}>{svc.key}</code>
                    </div>
                    <span style={{
                      fontSize: '11px', fontWeight: 600, padding: '3px 8px', borderRadius: 20,
                      background: (STATUS_COLORS[svc.status] || '#6b7280') + '22',
                      color: STATUS_COLORS[svc.status] || '#6b7280',
                    }}>{STATUS_LABELS[svc.status] || svc.status}</span>
                  </div>
                  <div style={{ fontSize: '13px', color: 'var(--admin-text-secondary)', marginBottom: '12px' }}>
                    <i className="fa-solid fa-users" style={{ marginRight: 5 }}></i>
                    {svcCount?.count || 0} signup{svcCount?.count !== 1 ? 's' : ''}
                    {svc.launch_date && (
                      <span style={{ marginLeft: 10 }}>
                        <i className="fa-solid fa-calendar" style={{ marginRight: 4 }}></i>{svc.launch_date}
                      </span>
                    )}
                    {svc.badge && (
                      <span style={{ marginLeft: 10, background: '#40E0D022', color: '#40E0D0', padding: '1px 6px', borderRadius: 4, fontSize: '10px', fontWeight: 600 }}>
                        {svc.badge}
                      </span>
                    )}
                  </div>
                  {isEditing ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      <select className="admin-select" value={editFields.status}
                        onChange={e => setEditFields(p => ({ ...p, status: e.target.value }))}>
                        <option value="coming_soon">Coming Soon</option>
                        <option value="presale">Pre-sale</option>
                        <option value="active">Live (Active)</option>
                        <option value="maintenance">Maintenance</option>
                      </select>
                      <input className="admin-input" type="text"
                        placeholder="Badge (e.g. NEW, BETA, SOON)" value={editFields.badge}
                        onChange={e => setEditFields(p => ({ ...p, badge: e.target.value }))} />
                      <input className="admin-input" type="text"
                        placeholder="Launch date (e.g. Q2 2025)" value={editFields.launch_date}
                        onChange={e => setEditFields(p => ({ ...p, launch_date: e.target.value }))} />
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button className="admin-btn admin-btn--sm admin-btn--primary" onClick={() => saveServiceEdit(svc.key)} disabled={savingService}>
                          {savingService ? '...' : 'Save'}
                        </button>
                        <button className="admin-btn admin-btn--sm admin-btn--ghost" onClick={() => setEditingService(null)}>Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      <button className="admin-btn admin-btn--sm admin-btn--secondary" onClick={() => openServiceEdit(svc)}>
                        <i className="fa-solid fa-pen"></i> Edit Status
                      </button>
                      {(svcCount?.count || 0) > 0 && (
                        <button className="admin-btn admin-btn--sm admin-btn--primary" onClick={() => openNotify(svc.key)}>
                          <i className="fa-solid fa-paper-plane"></i> Notify All
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Entries table */}
      <div className="admin-card">
        <div className="admin-flex-between" style={{ marginBottom: '12px' }}>
          <h3 className="admin-card-title">
            Entries
            {filteredEntries.length > 0 && (
              <span style={{ marginLeft: 8, color: 'var(--admin-text-muted)', fontWeight: 400, fontSize: '13px' }}>
                ({filteredEntries.length})
              </span>
            )}
          </h3>
          <div className="admin-btn-group">
            <button className="admin-btn admin-btn--sm admin-btn--secondary" onClick={loadAll} disabled={loading}>
              <i className="fa-solid fa-rotate"></i> Refresh
            </button>
            <button className="admin-btn admin-btn--sm admin-btn--secondary" onClick={exportCSV}>
              <i className="fa-solid fa-download"></i> Export CSV
            </button>
            {/* Notify All works for ANY selected waitlist, not just pre-launch services. */}
            {serviceFilter !== 'all' && filteredEntries.length > 0 && (
              <button className="admin-btn admin-btn--sm admin-btn--primary" onClick={() => openNotify(serviceFilter)}>
                <i className="fa-solid fa-paper-plane"></i> Notify All
              </button>
            )}
            {serviceFilter !== 'all' && filteredEntries.length > 0 && (
              <button className="admin-btn admin-btn--sm admin-btn--danger" onClick={() => clearServiceWaitlist(serviceFilter)}>
                <i className="fa-solid fa-trash"></i> Clear All
              </button>
            )}
          </div>
        </div>

        <div className="admin-filter-pills" style={{ marginBottom: '14px' }}>
          <button className={`admin-pill${serviceFilter === 'all' ? ' admin-pill--active' : ''}`}
            onClick={() => setServiceFilter('all')}>All ({entries.length})</button>
          {serviceKeysWithEntries.map(key => {
            const svc = services.find(s => s.key === key);
            const cnt = entries.filter(e => e.service_key === key).length;
            return (
              <button key={key} className={`admin-pill${serviceFilter === key ? ' admin-pill--active' : ''}`}
                onClick={() => setServiceFilter(key)}>
                {svc?.name || key} ({cnt})
              </button>
            );
          })}
        </div>

        {loading
          ? <p style={{ color: 'var(--admin-text-secondary)', fontSize: '13px' }}>Loading...</p>
          : <DataTable columns={columns} data={filteredEntries} emptyMessage="No waitlist entries" emptyIcon="fa-solid fa-inbox" />
        }
      </div>

      {/* Notify modal */}
      {notifyService && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'var(--admin-surface)', border: '1px solid var(--admin-border)', borderRadius: '12px', padding: '28px', width: '100%', maxWidth: '520px', margin: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ margin: 0, fontSize: '16px', color: 'var(--admin-text-primary)' }}>
                <i className="fa-solid fa-paper-plane" style={{ marginRight: 8, color: '#40E0D0' }}></i>
                Notify {notifyService.name} Waitlist
              </h3>
              <button className="admin-btn admin-btn--sm admin-btn--ghost" onClick={() => setNotifyService(null)}>
                <i className="fa-solid fa-xmark"></i>
              </button>
            </div>
            <p style={{ color: 'var(--admin-text-secondary)', fontSize: '13px', margin: '0 0 16px' }}>
              Sends to <strong style={{ color: 'var(--admin-text-primary)' }}>
                {counts.find(c => c.service_key === notifyService.key)?.count || 0}
              </strong> subscriber{counts.find(c => c.service_key === notifyService.key)?.count !== 1 ? 's' : ''}.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div>
                <label className="admin-label">SUBJECT *</label>
                <input className="admin-input" type="text" value={notifySubject}
                  onChange={e => setNotifySubject(e.target.value)} placeholder="e.g. We're live!" />
              </div>
              <div>
                <label className="admin-label">MESSAGE *</label>
                <textarea className="admin-input" rows={5} value={notifyMessage}
                  onChange={e => setNotifyMessage(e.target.value)}
                  placeholder="Write your launch announcement..."
                  style={{ resize: 'vertical' }} />
              </div>
              <div>
                <label className="admin-label">LAUNCH URL (optional)</label>
                <input className="admin-input" type="text" value={notifyLaunchUrl}
                  onChange={e => setNotifyLaunchUrl(e.target.value)} placeholder="https://..." />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 20, justifyContent: 'flex-end' }}>
              <button className="admin-btn admin-btn--secondary" onClick={() => setNotifyService(null)} disabled={notifySending}>Cancel</button>
              <button className="admin-btn admin-btn--primary" onClick={sendNotify} disabled={notifySending}>
                <i className="fa-solid fa-paper-plane"></i> {notifySending ? ' Sending...' : ' Send Notification'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
